import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Avatar from "../../../shared/Avatar";
import Card from "../../../shared/Card";
import Select from "../../../shared/Select";
import { EnvelopeIcon, PhoneIcon } from "@heroicons/react/20/solid";
import { PencilIcon } from "@heroicons/react/24/outline";
import { renderAmount } from "../../../utils";
import EditableText from "../../../shared/EditableText";
import Badge from "../../../shared/Badge";
import {
  fetchOrganizations,
  selectOrganizationsOptions,
} from "../../../redux/reducers/organizations/organizationsSlice";
import { useAuth } from "../../../hooks/useAuth";

const DriverCard = ({ info, handleUpdate }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();

  async function onSave(e, type) {
    let data = { ...e.data };
    if (type === "meta") {
      data = { meta: e.data };
    }
    handleUpdate(data);
  }

  const shortOrg = info.organization;

  return (
    <Card className="col-span-8 xl:col-span-2 p-6">
      <div className="flex xl:flex-col items-center justify-around my-4">
        <Avatar src={info.avatar} />
        <div className="my-4">
          {/* <DriverInfoEditable
            id="fullName"
            icon={UserIcon}
            value={info.fullName}
            save={(e) => onSave(e)}
          /> */}
          <DriverInfoEditable
            id="email"
            icon={EnvelopeIcon}
            value={info.email}
            save={(e) => onSave(e)}
          />
          <DriverInfoEditable
            id="phone"
            icon={PhoneIcon}
            value={info.phone}
            save={(e) => onSave(e)}
          />
        </div>
      </div>
      <hr />
      <div className="">
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-2 my-4 justify-items-start gap-3 ">
          <div>
            <h3 className="text-gray-500">Mileage Rate</h3>
            <DriverInfoEditable
              displayText={(e) => renderAmount(e)}
              id="mileageRate"
              value={info.meta?.mileageRate}
              save={(e) => onSave(e, "meta")}
              meta={true}
              type="number"
            />
          </div>
          <div>
            <h3 className="text-gray-500">Hourly Rate</h3>
            <DriverInfoEditable
              displayText={(e) => renderAmount(e)}
              id="hourlyRate"
              value={info.meta?.hourlyRate}
              save={(e) => onSave(e, "meta")}
              meta={true}
              type="number"
            />
          </div>
          <div>
            <h3 className="text-gray-500">Empty Rate</h3>
            <DriverInfoEditable
              displayText={(e) => renderAmount(e)}
              id="emptyRate"
              value={info.meta?.emptyRate}
              save={(e) => onSave(e, "meta")}
              meta={true}
              type="number"
            />
          </div>
          {info.org ? null : (
            <>
              <div>
                <h3 className="text-gray-500">License</h3>
                <DriverInfoEditable
                  id="license"
                  value={info.meta?.license}
                  save={(e) => onSave(e, "meta")}
                  meta={true}
                />
              </div>
              <div>
                <h3 className="text-gray-500">Organization</h3>
                <OrganizationEditable
                  organization={shortOrg}
                  onSave={(organization) =>
                    handleUpdate({ organization: organization?._id })
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

const DriverInfoEditable = ({ id, value, save, icon, type, displayText }) => {
  const Icon = icon;
  return (
    <div className="flex items-center">
      {icon && <Icon className="h-4 w-4 mr-1" />}
      <EditableText
        displayText={(e) => (
          <span className="text-base">{displayText ? displayText(e) : e}</span>
        )}
        id={id}
        iconClasses="h-3 w-3"
        value={value}
        onSave={(e) => save(e)}
        type={type}
      />
    </div>
  );
};

/**
 * Component for editing organization field
 * Shows badge by default, dropdown when editing
 */
const OrganizationEditable = ({ organization, onSave }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(organization);

  // Get organizations from Redux store
  const organizationsStatus = useSelector(
    (state) => state.organizations.status
  );
  const organizationOptions = useSelector(selectOrganizationsOptions);

  // Fetch organizations if not already loaded
  useEffect(() => {
    if (organizationsStatus === "idle") {
      dispatch(fetchOrganizations({ parent: user.parentCompany }));
    }
  }, [organizationsStatus, dispatch, user.parentCompany]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    onSave(selectedOrg);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setSelectedOrg(organization);
    setIsEditing(false);
  };

  const handleOrganizationChange = ({ value }) => {
    setSelectedOrg(value);
  };

  // Get the display value for the Select component
  const getSelectDisplayValue = () => {
    if (!selectedOrg) return "";
    return selectedOrg.name || selectedOrg.label || "";
  };

  if (isEditing) {
    return (
      <div className="space-y-3 min-w-0">
        <div className="min-w-[250px]">
          <Select
            value={getSelectDisplayValue()}
            placeholder="Select an organization"
            options={organizationOptions}
            onChange={handleOrganizationChange}
            className="text-sm"
          />
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleSave}
            className="text-xs px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-300 font-medium transition-colors duration-200"
          >
            Save
          </button>
          <button
            onClick={handleCancel}
            className="text-xs px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 font-medium transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2 min-w-0">
      <div className="flex-1 min-w-0">
        {organization ? (
          <Badge
            label={organization.name}
            link={`/organizations/${organization._id}`}
          />
        ) : (
          <span className="text-gray-400 italic text-sm">No organization</span>
        )}
      </div>
      <button
        onClick={handleEdit}
        className="text-gray-400 hover:text-primary-600 focus:outline-none focus:text-primary-600 transition-colors duration-200 flex-shrink-0"
        title="Edit organization"
      >
        <PencilIcon className="h-3 w-3" />
      </button>
    </div>
  );
};

export default DriverCard;
